import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Encounter } from '@medplum/fhirtypes'
import { INSURANCE_VERDICTS_MAX_SUBJECTS } from '@/model/middleware-types'
import { useClient, type OrpcClient } from './useClient'

export type InsuranceVerdictSubject = Parameters<
  OrpcClient['insuranceVerdicts']
>[0]['subjects'][number]
export type InsuranceVerdict = Awaited<
  ReturnType<OrpcClient['insuranceVerdicts']>
>['verdicts'][number]

const INSURANCE_VERDICTS_KEY = ['insurance-verdicts']

/**
 * The middleware's insurance verdict (payer eligibility, the clinic decision, and the roll-up)
 * for each subject, in order — the console never judges eligibility responses or clinic
 * decisions itself, so it shows what the provider app and Billing V2 show.
 */
function useVerdictsQuery<T>(
  subjects: InsuranceVerdictSubject[],
  select: (verdicts: InsuranceVerdict[]) => T,
) {
  const client = useClient()
  return useQuery({
    queryKey: [...INSURANCE_VERDICTS_KEY, subjects],
    queryFn: async () => {
      const batches: InsuranceVerdictSubject[][] = []
      for (let i = 0; i < subjects.length; i += INSURANCE_VERDICTS_MAX_SUBJECTS) {
        batches.push(subjects.slice(i, i + INSURANCE_VERDICTS_MAX_SUBJECTS))
      }
      const results = await Promise.all(
        batches.map((batch) => client.insuranceVerdicts({ subjects: batch })),
      )
      return results.flatMap((result) => result.verdicts)
    },
    enabled: subjects.length > 0,
    staleTime: 5 * 60 * 1000,
    select,
  })
}

/** One subject's verdict: a visit (`appointmentId`) or a patient (`patientId`, primary coverage). */
export function useInsuranceVerdict(subject: InsuranceVerdictSubject | null) {
  return useVerdictsQuery(subject ? [subject] : [], (verdicts) => verdicts[0])
}

/** Verdicts keyed by appointment id, for a list of visits. */
export function useAppointmentInsuranceVerdicts(appointmentIds: string[]) {
  const ids = [...new Set(appointmentIds)].sort()
  const query = useVerdictsQuery(
    ids.map((appointmentId) => ({ appointmentId })),
    (verdicts): Record<string, InsuranceVerdict> =>
      Object.fromEntries(ids.map((id, index) => [id, verdicts[index]])),
  )
  return ids.length === 0 ? { ...query, data: {} as Record<string, InsuranceVerdict> } : query
}

/**
 * Verdicts keyed by encounter id, for the Schedule's encounters (each judged on its linked
 * appointment). An encounter without an appointment has no entry.
 */
export function useEncounterInsuranceVerdicts(encounters: Encounter[]) {
  // Sort for a stable query key regardless of encounter ordering.
  const visits = encounters
    .flatMap((encounter) => {
      const appointmentId = encounter.appointment?.[0]?.reference?.split('/')[1]
      return encounter.id && appointmentId ? [{ encounterId: encounter.id, appointmentId }] : []
    })
    .sort((a, b) => a.encounterId.localeCompare(b.encounterId))
  const query = useVerdictsQuery(
    visits.map(({ appointmentId }) => ({ appointmentId })),
    (verdicts): Record<string, InsuranceVerdict> =>
      Object.fromEntries(visits.map(({ encounterId }, index) => [encounterId, verdicts[index]])),
  )
  return visits.length === 0 ? { ...query, data: {} as Record<string, InsuranceVerdict> } : query
}

/** Refetch every verdict on screen — after an eligibility check or a clinic decision. */
export function useInvalidateInsuranceVerdicts() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: INSURANCE_VERDICTS_KEY })
}
